import '@total-typescript/ts-reset';
import type { ThemeSettingsState } from '@sites/site-theme/src/state/theme.types';
import type { ThemeFont, ThemeSelectedFonts } from '@sites/site-theme/src/types/theme-font.type';
import type { ThemeColor, ThemeSelectedColors } from '@sites/site-theme/src/types/theme-color.type';
import type { ThemeButton, ThemeSelectedButtons } from '@sites/site-theme/src/types/theme-button.type';

import {
    cssSizeToString,
    formatThemeFontProperty,
    type ThemeFontProperty,
} from '@sites/site-theme/src/helpers/font-css';
import type { ThemeCssSize } from '@sites/site-theme/src/entity/schemas';
import type { ThemeBreakpoint } from '@sites/site-theme/src/helpers/responsive';

import {
    THEME_FONTS_VARIABLES,
    THEME_BUTTONS_VARIABLES,
    THEME_COLORS_VARIABLES,
    THEME_BUTTONS_HOVER_VARIABLES,
} from '@sites/site-theme/src/config/css-variables';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import type { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';
import type {
    ThemeButtonHoverProperties,
    ThemeButtonProperties,
} from '@sites/site-theme/src/enums/button-properties.enum';

/**
 * Desktop writes the bare variable; the other two append their own suffix.
 *
 * A suffixed variable is written only where the font declares a value for that breakpoint — see
 * `formatThemeFontProperty` on why an inherited value must not be written here.
 */
/**
 * A stored button property as a CSS value.
 *
 * `borderRadius` and `borderWidth` are `{ value, unit }` since v0; everything else on a button is
 * already a scalar. This used to be a bare `.toString()`, which on the sized ones would have
 * emitted `[object Object]` into the stylesheet — and silently, because `toString` exists on every
 * object and the compiler had nothing to object to.
 */
function toCssValue(value: ThemeCssSize | string | number): string {
    return typeof value === 'object' ? cssSizeToString(value) : String(value);
}

const SUFFIX_BY_BREAKPOINT: ReadonlyArray<readonly [ThemeBreakpoint, string]> = [
    ['desktop', ''],
    ['tablet', '-tablet'],
    ['mobile', '-mobile'],
];

export function formatThemeColorVariables(
    values: Record<string, string>,
    colors: ThemeSelectedColors | undefined
): Record<string, string> {
    if (!colors) {
        return values;
    }

    return {
        ...values,
        ...colors.selected.reduce((colorValue: Record<string, string>, color: ThemeColor) => {
            if (!THEME_COLORS_VARIABLES[color.name as ThemeColorNames]) {
                return colorValue;
            }

            return {
                ...colorValue,
                [THEME_COLORS_VARIABLES[color.name as ThemeColorNames]]: color.value,
            };
        }, {}),
    };
}

export function formatThemeFontVariables(
    values: Record<string, string>,
    fonts: ThemeSelectedFonts | undefined
): Record<string, string> {
    if (!fonts) {
        return values;
    }

    return {
        ...values,
        ...fonts.selected.reduce((fontValue: Record<string, string>, font: ThemeFont) => {
            const tagVariables = THEME_FONTS_VARIABLES[font.tag];

            if (!tagVariables) {
                return fontValue;
            }

            (Object.keys(tagVariables) as ThemeFontProperties[]).forEach((prop) => {
                const variable = tagVariables[prop];

                if (!variable || (prop === ThemeFontProperties.color && font.tag === ThemeFontTags.button)) {
                    return;
                }

                SUFFIX_BY_BREAKPOINT.forEach(([breakpoint, suffix]) => {
                    const value = formatThemeFontProperty(font, prop as ThemeFontProperty, breakpoint);

                    if (value === undefined) {
                        return;
                    }

                    // A palette name (`dark`) becomes `var(--dark-color)`, so text follows later edits
                    // to the theme colour; a literal colour passes through unchanged.
                    fontValue[`${variable}${suffix}`] =
                        prop === ThemeFontProperties.color ? themeColorCss(value) : value;
                });
            });

            return fontValue;
        }, {}),
    };
}

export function formatThemeButtonVariables(
    values: Record<string, string>,
    buttons: ThemeSelectedButtons | undefined
): Record<string, string> {
    if (!buttons) {
        return values;
    }

    return {
        ...values,
        ...buttons.selected.reduce((buttonValues: Record<string, string>, button: ThemeButton) => {
            const buttonStyleTypeVariables = THEME_BUTTONS_VARIABLES[button.styleType]!;
            const buttonHoverStyleTypeVariables = THEME_BUTTONS_HOVER_VARIABLES[button.styleType];

            const buttonKeys = Object.keys(buttonStyleTypeVariables) as ThemeButtonProperties[];
            buttonKeys.forEach((prop) => {
                const buttonStyleTypeVariable = buttonStyleTypeVariables[prop];
                const buttonBasePropValue = button.base?.[prop];

                if (Array.isArray(buttonStyleTypeVariable) && Array.isArray(buttonBasePropValue)) {
                    buttonStyleTypeVariable.forEach((variable: string, index: number) => {
                        const value = buttonBasePropValue[index] || buttonBasePropValue[0]!;

                        if (THEME_COLORS_VARIABLES[value as ThemeColorNames]) {
                            buttonValues[variable] = values[THEME_COLORS_VARIABLES[value as ThemeColorNames]]!;
                            return;
                        }

                        buttonValues[variable] = value;
                    });
                } else if (!Array.isArray(buttonStyleTypeVariable) && !Array.isArray(buttonBasePropValue)) {
                    buttonValues[buttonStyleTypeVariable] = toCssValue(buttonBasePropValue);
                }
            });

            if (!button.hover) {
                return buttonValues;
            }

            const hoverKeys = Object.keys(buttonHoverStyleTypeVariables!) as ThemeButtonHoverProperties[];
            hoverKeys.forEach((prop) => {
                const buttonHoverStyleTypeVariable = buttonHoverStyleTypeVariables?.[prop];
                const buttonHoverPropValue = button.hover?.[prop];

                if (Array.isArray(buttonHoverStyleTypeVariable) && Array.isArray(buttonHoverPropValue)) {
                    buttonHoverStyleTypeVariable.forEach((variable: string, index: number) => {
                        const value = buttonHoverPropValue[index] || buttonHoverPropValue[0]!;

                        if (THEME_COLORS_VARIABLES[value as ThemeColorNames]) {
                            buttonValues[variable] = values[THEME_COLORS_VARIABLES[value as ThemeColorNames]]!;
                            return;
                        }

                        buttonValues[variable] = value;
                    });
                } else if (!Array.isArray(buttonHoverStyleTypeVariable) && !Array.isArray(buttonHoverPropValue)) {
                    buttonValues[buttonHoverStyleTypeVariable] = toCssValue(buttonHoverPropValue);
                }
            });

            return buttonValues;
        }, {}),
    };
}

/**
 * Every CSS custom property the theme resolves to.
 *
 * This is what the strategy layer existed to provide. `AbstractBaseThemeStrategy` +
 * `WebsiteThemeStrategy` + `AbstractThemeFormatter` + `WebThemeFormatter` — four classes and two
 * injection tokens — answered "which variant is this" for a package that now has one, and the only
 * thing anyone ever asked them for was this function.
 *
 * The old `formatTheme` called the button pass twice, once as "buttons" and once as
 * "buttonsHover", with the same arguments; the second call re-derived the same keys from the same
 * input and is left out.
 */
export function formatThemeVariables(theme: ThemeSettingsState): Record<string, string> {
    let values: Record<string, string> = {};

    values = formatThemeColorVariables(values, theme.colors);
    values = formatThemeFontVariables(values, theme.fonts);
    values = formatThemeButtonVariables(values, theme.buttons);

    return values;
}
